import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Copy, ExternalLink } from 'lucide-react';
import { Button, Modal, Input, Loading, EmptyState, Badge } from '@/components/ui';
import { formatDate } from '@/utils/date';
import {
  affiliateApi,
  startUrl,
  qrImageUrl,
  type AffiliateLinkRow,
  type CreateLinkInput,
} from '@/api/affiliate';

const JOIN_PLANS = ['ignite', 'accelerate', 'command', 'business'];

function copy(text: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success('Copied'),
    () => toast.error('Copy failed'),
  );
}

export default function SignupLinksTab({ partnerId }: { partnerId: string }) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: links, isLoading } = useQuery({
    queryKey: ['affiliate-links', partnerId],
    queryFn: () => affiliateApi.links.listByPartner(partnerId),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      affiliateApi.links.toggleActive(id, active),
    onSuccess: () => {
      toast.success('Link updated');
      qc.invalidateQueries({ queryKey: ['affiliate-links', partnerId] });
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-secondary">
          Registration links. Each link can bind a specific agreement, so signups inherit those terms.
        </p>
        <Button size="sm" onClick={() => setModalOpen(true)}>
          <Plus size={15} className="mr-1" /> Create link
        </Button>
      </div>

      {isLoading ? (
        <Loading text="Loading links…" />
      ) : !(links ?? []).length ? (
        <EmptyState
          title="No signup links yet"
          description="Create a link the partner can share to open trials bound to an agreement."
          actionLabel="Create link"
          onAction={() => setModalOpen(true)}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {links!.map((l) => (
            <LinkCard key={l.id} link={l} onToggle={(active) => toggleMut.mutate({ id: l.id, active })} />
          ))}
        </div>
      )}

      <CreateLinkModal
        open={modalOpen}
        partnerId={partnerId}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}

function LinkCard({ link, onToggle }: { link: AffiliateLinkRow; onToggle: (active: boolean) => void }) {
  const url = startUrl(link.code);
  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-white font-medium">{link.label}</span>
        <Badge variant={link.is_active ? 'success' : 'neutral'}>
          {link.is_active ? 'active' : 'inactive'}
        </Badge>
        <button
          type="button"
          onClick={() => onToggle(!link.is_active)}
          className="ms-auto text-xs text-indigo-300 hover:text-indigo-200"
        >
          {link.is_active ? 'Deactivate' : 'Activate'}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <img
          src={qrImageUrl(url, 96)}
          alt={`QR for ${link.code}`}
          width={96}
          height={96}
          className="rounded bg-white p-1 flex-shrink-0"
        />
        <div className="min-w-0 space-y-1 text-xs">
          <div className="text-ink-muted">
            Plan <span className="capitalize text-ink-secondary">{link.join_plan_code}</span> ·{' '}
            <span className="num">{link.join_free_days}</span>d free
          </div>
          <div className="font-mono text-ink-secondary break-all">{url}</div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => copy(url)}
              className="inline-flex items-center gap-1 text-indigo-300 hover:text-indigo-200"
            >
              <Copy size={13} /> Copy
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-indigo-300 hover:text-indigo-200"
            >
              <ExternalLink size={13} /> Open
            </a>
          </div>
          <div className="text-ink-muted pt-1">
            Uses <span className="num">{link.use_count}</span>
            {link.max_uses != null ? ` / ${link.max_uses}` : ''}
            {link.expires_at ? ` · expires ${formatDate(link.expires_at)}` : ''}
            {link.agreement_id ? ' · bound agreement' : ' · partner default agreement'}
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateLinkModal({
  open,
  partnerId,
  onClose,
}: {
  open: boolean;
  partnerId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [code, setCode] = useState('');
  const [joinPlan, setJoinPlan] = useState('ignite');
  const [joinFreeDays, setJoinFreeDays] = useState('30');
  const [agreementId, setAgreementId] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    if (open) {
      setLabel('');
      setCode('');
      setJoinPlan('ignite');
      setJoinFreeDays('30');
      setAgreementId('');
      setMaxUses('');
      setExpiresAt('');
    }
  }, [open]);

  const { data: agreements } = useQuery({
    queryKey: ['affiliate-agreements', partnerId],
    queryFn: () => affiliateApi.agreements.listByPartner(partnerId),
    enabled: open,
  });

  const createMut = useMutation({
    mutationFn: () => {
      const payload: CreateLinkInput = {
        label,
        code,
        partner_id: partnerId,
        agreement_id: agreementId || null,
        join_plan_code: joinPlan,
        join_free_days: Number.parseInt(joinFreeDays, 10) || 30,
        max_uses: maxUses.trim() ? Number.parseInt(maxUses, 10) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      };
      return affiliateApi.links.create(payload);
    },
    onSuccess: () => {
      toast.success('Link created');
      qc.invalidateQueries({ queryKey: ['affiliate-links', partnerId] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  return (
    <Modal isOpen={open} onClose={onClose} title="Create signup link">
      <div className="space-y-4">
        <Input label="Label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Yan — Ignite trial" required />
        <Input
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="YAN2026"
          hint="4–20 uppercase letters or digits. Used in the URL."
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1.5">Join plan</label>
            <select
              value={joinPlan}
              onChange={(e) => setJoinPlan(e.target.value)}
              className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm capitalize"
            >
              {JOIN_PLANS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Free days"
            value={joinFreeDays}
            onChange={(e) => setJoinFreeDays(e.target.value)}
            type="number"
            min={0}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-secondary mb-1.5">Bind agreement</label>
          <select
            value={agreementId}
            onChange={(e) => setAgreementId(e.target.value)}
            className="w-full h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
          >
            <option value="">Partner's effective agreement (default)</option>
            {(agreements ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.status} · from {formatDate(a.effective_from)} · {a.reward_type.replace(/_/g, ' ')} {a.commission_pct}%
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Max uses (blank = ∞)" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} type="number" min={1} />
          <Input label="Expires at (optional)" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} type="datetime-local" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!label.trim()) return toast.error('Label is required');
              if (!code.trim()) return toast.error('Code is required');
              createMut.mutate();
            }}
            loading={createMut.isPending}
          >
            Create link
          </Button>
        </div>
      </div>
    </Modal>
  );
}
